import React from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {DepartmentsEnum, IndustriesEnum} from '../../../models/api/userMetadataModel';

/*
 * Form props.
 */

export interface WelcomeCarouselFormContainerProps {
  onPreviousClick: () => void;
  onCompleteForm: (isSkip?: boolean) => void;
}

/*
 * Form styles.
 */

export const WelcomeCarouselDropdownPlaceholder = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

export const WelcomeCarouselDropdownError = styled.div`
  color: ${(p) => p.theme.palette[PaletteColorsEnum.RED].shade40};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  margin-top: -10px;
  height: 15px;
`;

export function computeDisplayNameForDepartment(department?: string) {
  switch (department) {
    case DepartmentsEnum.ACCOUNT_MANAGEMENT:
      return 'Account Management';

    case DepartmentsEnum.BROKERAGE:
      return 'Broker';

    case DepartmentsEnum.EXECUTIVE:
      return 'CEO or Owner';

    case DepartmentsEnum.CLIENT_SERVICES:
      return 'Client Services and Success';

    case DepartmentsEnum.CUSTOMER_SUPPORT:
      return 'Customer Support';

    case DepartmentsEnum.ENGINEERING:
      return 'Engineering and Product';

    case DepartmentsEnum.FINANCE:
      return 'Finance and Legal';

    case DepartmentsEnum.MARKETING:
      return 'Marketing and Design';

    case DepartmentsEnum.OPERATIONS:
      return 'Operations';

    case DepartmentsEnum.PROJECT_MANAGEMENT:
      return 'Project Management';

    case DepartmentsEnum.SALES:
      return 'Sales and Business Development';

    default:
      return 'Other';
  }
}

/*
 * Form items.
 */

export const DepartmentsDropdownItems = [
  {
    title: (
      <Trans id="persona-department-accountmanagement">
        {computeDisplayNameForDepartment(DepartmentsEnum.ACCOUNT_MANAGEMENT)}
      </Trans>
    ),
    key: DepartmentsEnum.ACCOUNT_MANAGEMENT,
  },
  {
    title: (
      <Trans id="persona-department-broker">
        {computeDisplayNameForDepartment(DepartmentsEnum.BROKERAGE)}
      </Trans>
    ),
    key: DepartmentsEnum.BROKERAGE,
  },
  {
    title: (
      <Trans id="persona-department-ceoowner">
        {computeDisplayNameForDepartment(DepartmentsEnum.EXECUTIVE)}
      </Trans>
    ),
    key: DepartmentsEnum.EXECUTIVE,
  },
  {
    title: (
      <Trans id="persona-department-clientservicessuccess">
        {computeDisplayNameForDepartment(DepartmentsEnum.CLIENT_SERVICES)}
      </Trans>
    ),
    key: DepartmentsEnum.CLIENT_SERVICES,
  },
  {
    title: (
      <Trans id="persona-department-customersupport">
        {computeDisplayNameForDepartment(DepartmentsEnum.CUSTOMER_SUPPORT)}
      </Trans>
    ),
    key: DepartmentsEnum.CUSTOMER_SUPPORT,
  },
  {
    title: (
      <Trans id="persona-department-engineeringproduct">
        {computeDisplayNameForDepartment(DepartmentsEnum.ENGINEERING)}
      </Trans>
    ),
    key: DepartmentsEnum.ENGINEERING,
  },
  {
    title: (
      <Trans id="persona-department-financelegal">
        {computeDisplayNameForDepartment(DepartmentsEnum.FINANCE)}
      </Trans>
    ),
    key: DepartmentsEnum.FINANCE,
  },
  {
    title: (
      <Trans id="persona-department-marketingdesign">
        {computeDisplayNameForDepartment(DepartmentsEnum.MARKETING)}
      </Trans>
    ),
    key: DepartmentsEnum.MARKETING,
  },
  {
    title: (
      <Trans id="persona-department-operations">
        {computeDisplayNameForDepartment(DepartmentsEnum.OPERATIONS)}
      </Trans>
    ),
    key: DepartmentsEnum.OPERATIONS,
  },
  {
    title: (
      <Trans id="persona-department-projectmanagement">
        {computeDisplayNameForDepartment(DepartmentsEnum.PROJECT_MANAGEMENT)}
      </Trans>
    ),
    key: DepartmentsEnum.PROJECT_MANAGEMENT,
  },
  {
    title: (
      <Trans id="persona-department-salesbusinessdevelopment">
        {computeDisplayNameForDepartment(DepartmentsEnum.SALES)}
      </Trans>
    ),
    key: DepartmentsEnum.SALES,
  },
  {
    title: (
      <Trans id="persona-department-other">{computeDisplayNameForDepartment(DepartmentsEnum.OTHER)}</Trans>
    ),
    key: DepartmentsEnum.OTHER,
  },
];

export function computeDisplayNameForIndustry(industry?: string) {
  switch (industry) {
    case IndustriesEnum.ECOMMERCE:
      return 'Ecommerce';

    case IndustriesEnum.EVENTS:
      return 'Events and Experiences';

    case IndustriesEnum.FINANCIAL_SERVICES:
      return 'Financial Services';

    case IndustriesEnum.LOGISTICS:
      return 'Logistics';

    case IndustriesEnum.MARKETING_ADVERTISING:
      return 'Marketing and Advertising';

    case IndustriesEnum.MANUFACTURING:
      return 'Manufacturing';

    case IndustriesEnum.PROFESSIONAL_SERVICES:
      return 'Professional Services';

    case IndustriesEnum.SAAS_TECHNOLOGY:
      return 'Software';

    case IndustriesEnum.TRAVEL_HOSPITALITY:
      return 'Travel and Hospitality';

    case IndustriesEnum.HEALTH_FITNESS:
      return 'Health and Fitness';

    default:
      return 'Other';
  }
}

export const IndustriesDropdownItems = [
  {
    title: (
      <Trans id="company-industry-ecommerce">{computeDisplayNameForIndustry(IndustriesEnum.ECOMMERCE)}</Trans>
    ),
    key: IndustriesEnum.ECOMMERCE,
  },
  {
    title: (
      <Trans id="company-industry-eventsexperiences">
        {computeDisplayNameForIndustry(IndustriesEnum.EVENTS)}
      </Trans>
    ),
    key: IndustriesEnum.EVENTS,
  },
  {
    title: (
      <Trans id="company-industry-financialservices">
        {computeDisplayNameForIndustry(IndustriesEnum.FINANCIAL_SERVICES)}
      </Trans>
    ),
    key: IndustriesEnum.FINANCIAL_SERVICES,
  },
  {
    title: (
      <Trans id="company-industry-logistics">{computeDisplayNameForIndustry(IndustriesEnum.LOGISTICS)}</Trans>
    ),
    key: IndustriesEnum.LOGISTICS,
  },
  {
    title: (
      <Trans id="company-industry-marketingadvertising">
        {computeDisplayNameForIndustry(IndustriesEnum.MARKETING_ADVERTISING)}
      </Trans>
    ),
    key: IndustriesEnum.MARKETING_ADVERTISING,
  },
  {
    title: (
      <Trans id="company-industry-manufacturing">
        {computeDisplayNameForIndustry(IndustriesEnum.MANUFACTURING)}
      </Trans>
    ),
    key: IndustriesEnum.MANUFACTURING,
  },
  {
    title: (
      <Trans id="company-industry-professionalservices">
        {computeDisplayNameForIndustry(IndustriesEnum.PROFESSIONAL_SERVICES)}
      </Trans>
    ),
    key: IndustriesEnum.PROFESSIONAL_SERVICES,
  },
  {
    title: (
      <Trans id="company-industry-software">
        {computeDisplayNameForIndustry(IndustriesEnum.SAAS_TECHNOLOGY)}
      </Trans>
    ),
    key: IndustriesEnum.SAAS_TECHNOLOGY,
  },
  {
    title: (
      <Trans id="company-industry-travelhospitality">
        {computeDisplayNameForIndustry(IndustriesEnum.TRAVEL_HOSPITALITY)}
      </Trans>
    ),
    key: IndustriesEnum.TRAVEL_HOSPITALITY,
  },
  {
    title: (
      <Trans id="company-industry-healthfitness">
        {computeDisplayNameForIndustry(IndustriesEnum.HEALTH_FITNESS)}
      </Trans>
    ),
    key: IndustriesEnum.HEALTH_FITNESS,
  },
  {
    title: <Trans id="company-industry-other">{computeDisplayNameForIndustry(IndustriesEnum.OTHER)}</Trans>,
    key: IndustriesEnum.OTHER,
  },
];
